// ========== HUD ==========
// Screen-space overlay: leaderboard (top-right, cached), radar (top-left), mass (bottom-left),
// room line when online, the event countdown pill and power-up chips (bottom-centre, lifted above
// the touch buttons). Reads only the Scene, so solo and online look the same. Everything stays inside
// the safe area (notches, home indicator) and, in touch mode, clear of the DOM controls drawn over the
// canvas: the Exit button at the top centre and the Split/Feed/Boost cluster bottom-right.
import { EVENTS, POWERUPS, POWERUP_TYPES, WORLD, type EventType } from '../../shared/config';
import type { Scene, SceneCell } from '../../shared/types';
import { FONT, ICON_BOX, iconSprite, makeCanvas, ctx2d, textSprite, type TextImage } from './sprites';
import type { Camera } from './fx';

export const RADAR_RANGE = 1600; // world units shown from the player to the radar edge
export const HUD_PAD = 16;
export const NARROW_W = 600; // smaller screen side below this (phones, either way up): slimmer board, toasts under the HUD
export const BOARD_EVERY = 0.25; // leaderboard redraws per second: 4
// touch controls in styles.css, measured from the safe area: the Split/Feed/Boost cluster reaches 164 px up
// from the bottom (#tSplit), the Exit button is 36 px wide at top 10 px
export const TOUCH_CLUSTER_H = 164 + 8; // + a gap
export const EXIT_HALF_W = 18 + 8; // half its width + a gap
export const EXIT_CLEAR_H = 10 + 36 + 10; // toasts start below it
const INSETS_EVERY = 1; // s: re-read the safe-area insets (rotating 180° flips them without a resize)
// Sentry Field glass (as the lobby's cards) and the radar's three meanings: me, can eat me, the rest
const GLASS = 'rgba(55,22,75,0.58)', GLASS_LINE = 'rgba(255,240,225,0.22)', INK = '#fff7ef', INK_DIM = 'rgba(255,247,239,0.62)';
const GOLD = '#ffcf7a';
export const RADAR_ME = GOLD, RADAR_THREAT = '#ff6b6b', RADAR_OTHER = 'rgba(255,240,225,0.55)';

// per-frame inputs the renderer fills in (one reused object)
export interface HudFrame {
  W: number; H: number; d: number; // CSS px viewport, device pixel ratio
  t: number; // effect clock, seconds
  cam: Camera;
  cx: number; cy: number; // radar centre: my mass centre, or the camera when dead
  lift: number; // px to keep clear of the touch buttons
  dispR: (c: SceneCell) => number; // displayed (eased) radius
}

export function panel(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r = 12): void {
  g.beginPath(); g.roundRect(x, y, w, h, r);
  g.fillStyle = GLASS; g.fill();
  g.strokeStyle = GLASS_LINE; g.lineWidth = 1; g.stroke();
}

// far-away cells for the radar (online snapshots only carry what's near me): Scene.radar, from RADAR (0x17)
export type RadarDot = NonNullable<Scene['radar']>[number];
const massOfR = (r: number) => { const k = (r - 4) / 6; return k > 0 ? k * k : 0; }; // inverse of rad()

// env(safe-area-inset-*), mirrored by styles.css into --sa-t/r/b/l on :root (0 where unsupported or no DOM)
export interface Insets { t: number; r: number; b: number; l: number }
export function readInsets(): Insets {
  const out: Insets = { t: 0, r: 0, b: 0, l: 0 };
  try {
    const root = document.documentElement;
    if (!root || typeof getComputedStyle !== 'function') return out;
    const cs = getComputedStyle(root);
    const px = (k: string) => { const v = parseFloat(cs.getPropertyValue(k)); return v > 0 && v < 400 ? v : 0; };
    out.t = px('--sa-t'); out.r = px('--sa-r'); out.b = px('--sa-b'); out.l = px('--sa-l');
  } catch { /* no DOM (tests, workers) */ }
  return out;
}

export interface BoardRow { rank: number; name: string; mass: number; me: boolean }
// phones get a slimmer top 5; if I'm outside the shown rows, my own row is appended with my real rank.
// cap: at most this many rows in all (short touch screens), my own row included.
export function boardRows(scene: Scene, narrow: boolean, cap = Infinity): BoardRow[] {
  const lb = scene.leaderboard, rows: BoardRow[] = [];
  let top = Math.max(0, Math.min(narrow ? 5 : 10, cap));
  if (scene.alive && scene.myRank >= top && top + 1 > cap) top = Math.max(0, top - 1); // room for my row
  for (let i = 0; i < lb.length && i < top; i++) rows.push({ rank: i, name: lb[i].name, mass: lb[i].mass, me: lb[i].me });
  if (scene.alive && scene.myRank >= top && rows.length < cap) {
    const mine = lb[scene.myRank];
    const name = mine && mine.me ? mine.name : (scene.myCells.length ? scene.myCells[0].name : 'You');
    rows.push({ rank: scene.myRank, name, mass: mine && mine.me ? mine.mass : scene.myMass, me: true });
  }
  return rows;
}

export class Hud {
  bottom = 0; // toasts start below the HUD on narrow screens (0 = use the default top)
  // The leaderboard is re-drawn 4 times a second into its own canvas: cheaper, and the rows
  // stop flickering when two blobs have nearly the same mass.
  private board = { cv: null as HTMLCanvasElement | null, next: 0, w: 200, h: 0, dpr: 0, narrow: false, cap: Infinity };
  private ins = { t: 0, r: 0, b: 0, l: 0, W: -1, H: -1, at: -Infinity };
  private pill: { img: TextImage | null; type: EventType | null; secs: number; d: number } = { img: null, type: null, secs: -1, d: 0 };
  private massText = { n: -1, s: '', w: 0 };
  private roomText = { room: '', humans: -1, s: '', w: 0 };

  // the safe-area insets, re-read on a resize and once a second
  private insets(W: number, H: number, t: number): Insets {
    const i = this.ins;
    if (i.W !== W || i.H !== H || !(t - i.at < INSETS_EVERY) || t < i.at) {
      const r = readInsets();
      i.t = r.t; i.r = r.r; i.b = r.b; i.l = r.l; i.W = W; i.H = H; i.at = t;
    }
    return i;
  }

  private renderBoard(scene: Scene, d: number, narrow: boolean, t: number, lw: number, cap: number): void {
    const rows = boardRows(scene, narrow, cap);
    const rowH = narrow ? 18 : 20, first = narrow ? 42 : 46;
    const maxName = narrow ? Math.max(5, 10 - Math.round((150 - lw) / 10)) : 14; // a squeezed board shows shorter names
    const lh = first - 6 + rows.length * rowH + 8;
    const b = this.board;
    const cv = b.cv && b.dpr === d && b.h === lh && b.w === lw ? b.cv : makeCanvas(lw * d, lh * d);
    const g = ctx2d(cv);
    g.setTransform(d, 0, 0, d, 0, 0); g.clearRect(0, 0, lw, lh);
    panel(g, 0.5, 0.5, lw - 1, lh - 1);
    g.textBaseline = 'alphabetic';
    g.font = `600 10px ${FONT}`; g.fillStyle = INK_DIM; g.textAlign = 'left';
    g.fillText('L E A D E R B O A R D', 14, 24);
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i], y = first + i * rowH;
      g.font = `${r.me ? 700 : 500} ${narrow ? 12 : 13}px ${FONT}`;
      g.fillStyle = r.me ? GOLD : INK; g.textAlign = 'left';
      const name = r.name.length > maxName ? r.name.slice(0, maxName - 1) + '…' : r.name;
      g.fillText(`${r.rank + 1}  ${name}`, 14, y);
      g.textAlign = 'right'; g.fillStyle = r.me ? GOLD : INK_DIM;
      g.fillText(String(Math.floor(r.mass)), lw - 14, y);
    }
    b.cv = cv; b.next = t + BOARD_EVERY; b.w = lw; b.h = lh; b.dpr = d; b.narrow = narrow; b.cap = cap;
  }

  // leaderboard, radar, mass; sets this.bottom for the toasts
  draw(ctx: CanvasRenderingContext2D, scene: Scene, f: HudFrame): void {
    const { W, H, d, t, cam } = f, pad = HUD_PAD, b = this.board;
    // phones either way up: a landscape phone is wide but short
    const narrow = Math.min(W, H) < NARROW_W, touch = f.lift > 0, ins = this.insets(W, H, t);
    const top = pad + ins.t, left = pad + ins.l, right = W - pad - ins.r, bottom = H - pad - ins.b;
    const rs = narrow ? Math.min(120, W * 0.28) : Math.min(180, W * 0.32), half = rs / 2;

    // leaderboard, top-right
    let lw = narrow ? 150 : 200, cap = Infinity, beside = false;
    if (touch) {
      // narrower if it would reach the Exit button at the top centre (small portrait phones)...
      lw = Math.max(110, Math.min(lw, Math.floor(right - (W / 2 + EXIT_HALF_W))));
      // ...and only as many rows as fit above the Split/Feed/Boost cluster (landscape phones)
      const rowH = narrow ? 18 : 20, chrome = narrow ? 44 : 48;
      cap = Math.floor((H - ins.b - TOUCH_CLUSTER_H - top - chrome) / rowH);
      // not even one row fits above the buttons: move it beside the radar, out of their column
      if (cap < 1) { cap = Infinity; lw = narrow ? 150 : 200; beside = true; }
    }
    if (!b.cv || t >= b.next || b.dpr !== d || b.narrow !== narrow || b.w !== lw || b.cap !== cap) this.renderBoard(scene, d, narrow, t, lw, cap);
    if (b.cv) ctx.drawImage(b.cv, beside ? left + rs + 10 : right - b.w, top, b.w, b.h);

    // radar of the area around the player, top-left
    const rx = left, ry = top;
    this.bottom = narrow ? top + Math.max(rs, b.h) + 10 : ins.t ? top : 0;
    if (touch) this.bottom = Math.max(this.bottom, ins.t + EXIT_CLEAR_H);
    panel(ctx, rx, ry, rs, rs, 14);
    const k = half / RADAR_RANGE, cx = f.cx, cy = f.cy;
    ctx.save();
    ctx.beginPath(); ctx.roundRect(rx, ry, rs, rs, 14); ctx.clip();
    ctx.translate(rx + half, ry + half);
    // world border
    ctx.strokeStyle = 'rgba(255,240,225,0.4)'; ctx.lineWidth = 1.5;
    ctx.strokeRect(-cx * k, -cy * k, WORLD * k, WORLD * k);
    // view rectangle
    ctx.strokeStyle = 'rgba(255,207,122,0.45)'; ctx.lineWidth = 1;
    ctx.strokeRect(-W / 2 / cam.z * k, -H / 2 / cam.z * k, W / cam.z * k, H / cam.z * k);
    ctx.fillStyle = 'rgba(157,255,92,0.75)';
    for (const v of scene.viruses) {
      const dx = (v.x - cx) * k, dy = (v.y - cy) * k;
      if (Math.abs(dx) > half || Math.abs(dy) > half) continue;
      ctx.beginPath(); ctx.arc(dx, dy, Math.max(2, v.r * k), 0, Math.PI * 2); ctx.fill();
    }
    let myMax = 0;
    if (scene.alive) for (const c of scene.myCells) if (c.mass > myMax) myMax = c.mass;
    const me = scene.myId, big = myMax * 1.25;
    // far cells first (online): anything on screen is in scene.cells already and drawn over them below
    const radar: readonly RadarDot[] | undefined = scene.radar;
    if (radar && radar.length) {
      const vx = W / 2 / cam.z, vy = H / 2 / cam.z;
      for (const c of radar) {
        if (c.ownerId === me) continue;
        const ox = c.x - cam.x, oy = c.y - cam.y;
        if (Math.abs(ox) < vx && Math.abs(oy) < vy) continue;
        const dx = (c.x - cx) * k, dy = (c.y - cy) * k;
        if (!(Math.abs(dx) <= half + 10 && Math.abs(dy) <= half + 10)) continue; // also drops non-finite positions
        const r = c.r > 0 ? c.r : 0;
        ctx.beginPath(); ctx.arc(dx, dy, Math.max(2.5, r * k), 0, Math.PI * 2);
        ctx.fillStyle = massOfR(r) > big ? RADAR_THREAT : RADAR_OTHER;
        ctx.fill();
      }
    }
    for (const c of scene.cells) {
      const dx = (c.x - cx) * k, dy = (c.y - cy) * k;
      if (Math.abs(dx) > half + 10 || Math.abs(dy) > half + 10) continue;
      ctx.beginPath(); ctx.arc(dx, dy, Math.max(2.5, f.dispR(c) * k), 0, Math.PI * 2);
      // gold = you, red = can eat you, pale = you can eat it / even
      ctx.fillStyle = c.ownerId === me ? RADAR_ME : c.mass > big ? RADAR_THREAT : RADAR_OTHER;
      ctx.fill();
    }
    ctx.restore();

    // mass, bottom-left (and the room you're in when online, just above it), each on a glass pill so they
    // read over the bright floor
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    if (scene.alive) {
      const n = Math.floor(scene.myMass), m = this.massText;
      if (n !== m.n) { m.n = n; m.s = `Mass ${n}`; m.w = 0; }
      ctx.font = `600 14px ${FONT}`;
      if (!m.w) m.w = ctx.measureText(m.s).width;
      panel(ctx, left - 9, bottom - 17, m.w + 18, 23, 11.5);
      ctx.fillStyle = INK;
      ctx.fillText(m.s, left, bottom);
    }
    if (scene.online && scene.roomName) {
      const r = this.roomText, y = bottom - (scene.alive ? 30 : 0);
      if (r.room !== scene.roomName || r.humans !== scene.humans) {
        r.room = scene.roomName; r.humans = scene.humans; r.w = 0;
        r.s = `Room ${scene.roomName} · ${scene.humans} ${scene.humans === 1 ? 'player' : 'players'}`;
      }
      ctx.font = `600 12px ${FONT}`;
      if (!r.w) r.w = ctx.measureText(r.s).width;
      panel(ctx, left - 9, y - 15, r.w + 18, 21, 10.5);
      ctx.fillStyle = INK_DIM;
      ctx.fillText(r.s, left, y);
    }
  }

  // active event countdown pill; the label (with its emoji) is an image redrawn once a second
  drawEventPill(ctx: CanvasRenderingContext2D, scene: Scene, f: HudFrame): void {
    const ev = scene.event;
    if (!ev) return;
    const def = EVENTS[ev.type], W = f.W;
    const k = Math.max(0, Math.min(1, ev.dur > 0 ? ev.remaining / ev.dur : 0)), w = 220, x = W / 2 - w / 2, y = f.H - 92 - f.lift - this.ins.b;
    ctx.beginPath(); ctx.roundRect(x, y, w, 30, 15); ctx.fillStyle = GLASS; ctx.fill();
    ctx.strokeStyle = GLASS_LINE; ctx.lineWidth = 1; ctx.stroke();
    if (k > 0) {
      ctx.beginPath(); ctx.roundRect(x + 3, y + 3, (w - 6) * k, 24, 12); ctx.fillStyle = def.color;
      ctx.globalAlpha = 0.85; ctx.fill(); ctx.globalAlpha = 1;
    }
    const secs = Math.max(0, Math.ceil(ev.remaining)), p = this.pill;
    if (!p.img || p.type !== ev.type || p.secs !== secs || p.d !== f.d) {
      p.img = textSprite(`${def.label}  ${secs}s`, `700 12px ${FONT}`, '#f8fafc', 30, f.d, p.img);
      p.type = ev.type; p.secs = secs; p.d = f.d;
    }
    ctx.drawImage(p.img.cv, W / 2 - p.img.w / 2, y, p.img.w, p.img.h);
  }

  // my active power-ups, bottom-centre chips with draining rings (blink when nearly out)
  drawPowerChips(ctx: CanvasRenderingContext2D, scene: Scene, f: HudFrame): void {
    if (!scene.alive) return;
    const fx = scene.myFx;
    let n = 0;
    for (const k of POWERUP_TYPES) if ((fx[k] ?? 0) > 0) n++;
    if (!n) return;
    let i = 0;
    for (const k of POWERUP_TYPES) {
      const rem = fx[k] ?? 0;
      if (rem <= 0) continue;
      const def = POWERUPS[k], left = rem / def.dur;
      const x = f.W / 2 + (i++ - (n - 1) / 2) * 52, y = f.H - 34 - f.lift - this.ins.b;
      ctx.beginPath(); ctx.arc(x, y, 20, 0, Math.PI * 2); ctx.fillStyle = GLASS; ctx.fill();
      ctx.beginPath(); ctx.arc(x, y, 20, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, left));
      ctx.strokeStyle = def.color; ctx.lineWidth = 4; ctx.stroke();
      if (left < 0.25 && Math.sin(f.t * 20) < 0) continue;
      ctx.drawImage(iconSprite(def.icon), x - ICON_BOX / 2, y - ICON_BOX / 2, ICON_BOX, ICON_BOX);
    }
  }
}
